/**
 * The one recording player, shared by the Patient Advocate's Documents and the UMED case's Documents.
 *
 * Isaiah, 2026-10-01: PAs must be able to hear the recording where they read the case. Ruling 2026-10-30 (Isaiah Cotto,
 * SCRUM-123, tracker 1029-DG-U-02): "Yes docs and recordings should show on the UMED side" -- so a UMED operator plays
 * the same PA-vetted recording the same way, not through a second player with its own rules.
 *
 * The row asks the server for a fresh, audited link on every press (a link lives 300 s) and hands it to `start`.
 * An error after playback began is an expired link, not a format; a 410 closes the player (`gone`).
 */
import React, { useState } from 'react';

/** Every container a verbal approval arrives in: a phone, a handset, Teams. */
export const MEDIA_EXT = /\.(mp3|m4a|wav|webm|ogg|oga|aac|amr|3gp|mp4|mpeg|mpg|mov|wma|flac)$/i;

export interface PlayableDocument { content_type: string | null; document_class: string; original_filename: string | null }

/** Playable here: the type says audio or video, or a recording's file name does. */
export const isPlayable = (d: PlayableDocument) =>
  /^(audio|video)\//i.test(d.content_type || '') || (d.document_class === 'RECORDING' && MEDIA_EXT.test(d.original_filename || ''));

export type MediaState = 'idle' | 'started' | 'unplayable' | 'expired' | 'gone';

export function useRecordingPlayer() {
  const [media, setMedia] = useState<string | null>(null);
  const [mediaState, setMediaState] = useState<MediaState>('idle');
  return {
    media,
    mediaState,
    /** Play this freshly signed link. */
    start: (url: string) => { setMediaState('idle'); setMedia(url); },
    close: () => { setMedia(null); setMediaState('idle'); },
    /** 410: eMerge has the record but the file is not in storage. The player closes; the row says so in words. */
    gone: () => { setMedia(null); setMediaState('gone'); },
    /** A new press after a 410 asks again. */
    retry: () => { if (mediaState === 'gone') setMediaState('idle'); },
    onPlaying: () => setMediaState('started'),
    onError: () => setMediaState((s) => (s === 'started' ? 'expired' : 'unplayable')),
  };
}
export type RecordingPlayerState = ReturnType<typeof useRecordingPlayer>;

/**
 * The audio element and its two failure sentences, as children of the document row (which lays them out full width).
 * `tone` picks the workspace's classes: `pa-player` / `pa-notice`, `um-player` / `um-notice`.
 */
export function RecordingAudio({ player, name, tone }: { player: RecordingPlayerState; name: string; tone: 'pa' | 'um' }) {
  const notice = `${tone}-notice stop${tone === 'um' ? ' um-doc-err' : ''}`;
  return (
    <>
      {player.media && player.mediaState !== 'unplayable' && player.mediaState !== 'expired' && (
        // eslint-disable-next-line jsx-a11y/media-has-caption -- a phone recording has no caption track to offer
        <audio className={`${tone}-player`} controls autoPlay src={player.media} aria-label={`Recording: ${name}`}
          onPlaying={player.onPlaying}
          onError={player.onError} />
      )}
      {player.mediaState === 'unplayable' && <p className={notice} role="alert">This browser cannot play this file’s format. Use Open to download it and play it on the computer.</p>}
      {player.mediaState === 'expired' && <p className={notice} role="alert">The link to this recording expired while it was playing. Press Close player, then Play again.</p>}
    </>
  );
}
